import { account } from './account.js';
import { storage, settings } from './storage.js';
import { device } from './device.js';
import { i18n } from './i18n.js';

/* Soporte: tickets de problemas e ideas contra el servidor, con los topes que fija él. Quien escribe se identifica con
   la sesión de su cuenta (web y app con servidor). La app independiente con licencias añade otra vía, la licencia
   (core/support-license.js), poniéndola delante en `routes`.

   Una «vía» es { enabled(), ready(), base(), headers(), body(), fetch(url, opts) }:
     enabled  el servidor la ofrece (para mostrar la entrada en Ajustes)
     ready    hay con qué identificarse ahora mismo (sesión iniciada, licencia activa) */

export const support = {
    product: 'Filoteca',
    /** Clave del texto bajo «Soporte» en Ajustes. */
    subKey: 'support.subAccount',
    /** Si no hay con qué identificarse: { label(), run(onChange) } para ofrecer cómo conseguirlo (p. ej. activar la licencia). */
    gate: null,
    routes: [{
        id: 'account',
        enabled: () => !!(account.support && account.signedIn),
        ready: () => !!(account.support && account.signedIn && account.token),
        base: () => account.baseUrl(),
        headers: () => ({ Authorization: 'Bearer ' + account.token }),
        body: () => ({}),
        fetch: (url, opts) => fetch(url, opts)
    }],

    get enabled() { return this.routes.some((r) => r.enabled()); },
    ready() { return this.routes.some((r) => r.ready()); },

    async _post(path, body) {
        const route = this.routes.find((r) => r.ready());
        if (!route) return { ok: false, error: this.routes.length > 1 ? 'license' : 'session' };
        let r;
        try {
            const res = await route.fetch(route.base() + '/api/support/' + path, { method: 'POST', headers: Object.assign({ 'Content-Type': 'application/json' }, route.headers()), body: JSON.stringify(Object.assign(route.body(), body)), cache: 'no-store' });
            const json = await res.json().catch(() => null);
            // Un servidor sin soporte contesta con la web (HTML), no con JSON.
            r = json || { ok: false, error: 'unavailable' };
            if (!r.ok && !r.error) r.error = 'unavailable';
        } catch (e) {
            return { ok: false, error: 'network' };
        }
        return r;
    },

    async list() {
        const r = await this._post('list', {});
        if (r.ok) storage.set('supportUnread', r.unread || 0);
        return r;
    },
    get(id) { return this._post('get', { id }); },
    async create({ kind, title, body, includeDiag }) {
        return this._post('create', { kind, title, body, lang: i18n.lang, diag: includeDiag ? this.diagText() : '' });
    },
    reply(id, body) { return this._post('reply', { id, body }); },

    /** Datos técnicos que acompañan al ticket (solo si la persona lo marca): nada personal. */
    diagText() {
        const v = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';
        let native = 'no';
        try { native = window.cordova && window.cordova.plugins && window.cordova.plugins.filotecaPlayer ? (settings.get('nativePlayer') !== false ? 'sí' : 'desactivado') : 'no'; } catch (e) { /* sin plugin */ }
        const cs = getComputedStyle(document.documentElement);
        return [this.product + ' ' + v, device.platformLabel(), 'pantalla ' + screen.width + 'x' + screen.height + ' @' + (window.devicePixelRatio || 1), 'idioma ' + i18n.lang, 'reproductor nativo: ' + native, 'zonas seguras ' + (cs.getPropertyValue('--safe-top').trim() || '0') + '/' + (cs.getPropertyValue('--safe-bottom').trim() || '0'), navigator.userAgent.slice(0, 160)].join(' · ');
    },

    /** Respuestas sin leer guardadas en la última consulta (para mostrar en Ajustes sin ir al servidor). */
    unread() { return Number(storage.get('supportUnread')) || 0; },

    /** Al arrancar: mira si hay respuestas nuevas. Devuelve cuántas. */
    async checkUnread() {
        if (!this.ready() || navigator.onLine === false) return 0;
        const r = await this.list();
        return r.ok ? (r.unread || 0) : 0;
    }
};
